import React from 'react';
import { Zap, Layers, Home as HomeIcon, Building2, Lightbulb, Settings, CheckCircle2, Wrench, Gauge, Ruler } from 'lucide-react';
import { services } from '../mockData';
import { PageHero, SectionHeader, Reveal, TechCard, IconBox, BtnPrimary } from '../components/ui-kit';
import { CTA } from './Home';

const electricalServices = [
  { title: 'Installation électrique neuve', description: 'Conception et réalisation complète de votre installation électrique pour maisons neuves et locaux professionnels.', icon: HomeIcon },
  { title: 'Rénovation électrique', description: 'Mise à jour de votre installation existante selon les normes actuelles NF C 15-100.', icon: Settings },
  { title: 'Éclairage & Domotique', description: "Installation d'éclairage LED, systèmes domotiques intelligents pour plus de confort et d'économies.", icon: Lightbulb },
  { title: 'Dépannage électrique', description: "Service d'urgence disponible 7j/7 pour tous vos problèmes électriques.", icon: Zap }
];

const plasteringServices = [
  { title: 'Cloisons sèches', description: "Pose de cloisons en plaques de plâtre pour optimiser l'aménagement de vos espaces.", icon: Building2 },
  { title: 'Isolation thermique et phonique', description: "Amélioration de l'isolation de votre habitat pour plus de confort et d'économies d'énergie.", icon: Layers },
  { title: 'Plafonds suspendus', description: "Création de faux plafonds esthétiques avec intégration d'éclairage.", icon: HomeIcon },
  { title: 'Joints et finitions', description: 'Réalisation de joints parfaits et finitions impeccables pour un rendu professionnel.', icon: Ruler }
];

const specs = [
  { icon: Gauge, label: 'Norme', value: 'NF C 15-100' },
  { icon: Wrench, label: 'Dépannage', value: '7j / 7' },
  { icon: Ruler, label: 'Plâtrerie', value: 'DTU 25.41' },
  { icon: Lightbulb, label: 'Éclairage', value: 'LED & domotique' }
];

const ModuleGrid = ({ items, dark, tone }) => (
  <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
    {items.map((s, i) => (
      <Reveal key={s.title} delay={i * 80}>
        <TechCard dark={dark} className="h-full group">
          <div className="flex items-center justify-between">
            <IconBox icon={s.icon} tone={tone} />
            <span className={`font-mono text-xs ${dark ? 'text-slate-500' : 'text-slate-400'}`}>0{i + 1}</span>
          </div>
          <h3 className={`mt-6 font-display text-xl font-bold ${dark ? 'text-white' : 'text-ink'}`}>{s.title}</h3>
          <p className={`text-sm mt-3 leading-relaxed ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{s.description}</p>
        </TechCard>
      </Reveal>
    ))}
  </div>
);

const Services = () => (
  <div className="min-h-screen" data-testid="services-page">
    <PageHero
      testId="services-hero"
      eyebrow="Nos services"
      title={<>Électricité générale <span className="text-energy-gradient">&</span> travaux de plâtrerie</>}
      subtitle="De l'installation neuve à la rénovation complète, nous maîtrisons l'ensemble de la chaîne technique pour un résultat conforme, durable et esthétique."
      image="/assets/commercial1-tableau.webp"
    />

    <section className="bg-[#F8FAFC] text-ink py-24 lg:py-32 circuit-grid-light" data-testid="services-pillars">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader eyebrow="Deux expertises" title="Un seul interlocuteur pour tout votre second œuvre" />
        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={i * 120}>
              <TechCard className="h-full group p-8 lg:p-10">
                <div className="flex items-center gap-4">
                  <IconBox icon={s.icon === 'Zap' ? Zap : Layers} tone={i ? 'signal' : 'energy'} className="w-14 h-14" />
                  <h3 className="font-display text-2xl sm:text-3xl font-black">{s.title}</h3>
                </div>
                <p className="text-slate-600 mt-6 leading-relaxed">{s.description}</p>
                <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 size={18} className="text-energy shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
              </TechCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="relative bg-obsidian text-white py-24 lg:py-32 overflow-hidden noise" data-testid="services-electrical">
      <div className="absolute inset-0 circuit-grid opacity-60" />
      <div className="glow-orb w-[400px] h-[400px] bg-energy/15 -top-20 -right-20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader dark eyebrow="Électricité générale" title="Quatre modules, une installation parfaite" subtitle="De l'installation neuve à la rénovation, en passant par le dépannage, nous intervenons pour tous vos besoins électriques." />
        <ModuleGrid items={electricalServices} dark tone="energy" />
      </div>
    </section>

    <section className="bg-white text-ink py-24 lg:py-32" data-testid="services-plastering">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader eyebrow="Plâtrerie & isolation" title="Des volumes nets, des finitions impeccables" subtitle="Spécialistes de la pose de plaques de plâtre, nous réalisons tous vos travaux de cloisons, isolation et finitions." />
        <ModuleGrid items={plasteringServices} tone="signal" />
      </div>
    </section>

    <section className="bg-abyss text-white py-16 border-y border-white/10" data-testid="services-specs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {specs.map((s, i) => (
          <Reveal key={s.label} delay={i * 80} className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl border border-signal/30 bg-signal/10 text-signal flex items-center justify-center shrink-0">
              <s.icon size={20} />
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{s.label}</p>
              <p className="font-display text-lg font-bold">{s.value}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <CTA title="Besoin d'un devis ?" text="Contactez-nous pour une étude personnalisée et gratuite de votre projet." btn="Demander un devis gratuit" />
  </div>
);

export default Services;
